import { getTenantSetting } from '@/composables/useTenant';

const DEPOT_STROKE_COLOR = "#AAA163";
const MUSEUM_STROKE_COLOR = "#377266";

function parseIntegerSetting(value, fallback) {
    const parsedValue = parseInt(value, 10);

    return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

export const getPolygonStyles = (zoomLevel) => {
    const calculateWeight = (baseWeight) => {
        return Math.floor(baseWeight * (zoomLevel / 25));
    };

    const calculateInverseWeight = (minWeight, maxWeight) => {
        const minZoom = parseIntegerSetting(getTenantSetting("map.min_zoom"), 20);
        const maxZoom = parseIntegerSetting(getTenantSetting("map.max_zoom"), 25);

        if (!Number.isFinite(zoomLevel) || maxZoom <= minZoom) {
            return maxWeight;
        }

        const ratio = (zoomLevel - minZoom) / (maxZoom - minZoom);
        const clampedRatio = Math.min(1, Math.max(0, ratio));

        return Math.round(maxWeight - (maxWeight - minWeight) * clampedRatio);
    };

    return {
        background: {
            color: "#706C52",
            fillColor: "#706C52",
            fillOpacity: 0.3,
            weight: calculateWeight(1),
        },
        planimetry: {
            medagliere: {
                color: "#362E2A",
                fillColor: "#362E2A",
                fillOpacity: 1,
                opacity: 1,
                weight: 1.5,
            },
        },
        selected: {
            color: "#E8831C",
            fillColor: "#E8831C",
            fillOpacity: 1,
            opacity: 1,
            weight: calculateWeight(5),
        },
        transparent: {
            color: "#706C52",
            fillColor: "#706C52",
            fillOpacity: 0,
            weight: calculateWeight(1),
        },
        depot: {
            fillColor: "#E3E7DC",
            color: "#5D752C",
            weight: calculateWeight(4),
            opacity: 1,
            fillOpacity: 1,
        },
        hoverDepot: {
            fillColor: "#B5C3A6",
            color: "#5D752C",
            weight: calculateWeight(4),
            opacity: 1,
            fillOpacity: 1,
        },
        focusedDepot: {
            fillColor: "#ffffff",
            color: "#5D752C",
            weight: calculateWeight(4),
            opacity: 1,
            fillOpacity: 1,
        },
        environment: {
            fillColor: DEPOT_STROKE_COLOR,
            color: "#72683D",
            weight: 0,
            fillOpacity: 0.4,
        },
        benacci: {
            animali: {
                fillColor: "#ce7f29",
                color: "#362e2a",
                weight: calculateWeight(2),
                opacity: 1,
                fillOpacity: 0.8,
            },
            "campi etichette": {},
            note: {},
            tiranti: {
                fillColor: "#000000",
                color: "#000000",
                weight: calculateWeight(2),
            },
            to: {
                fillOpacity: 0.6,
                opacity: 1,
                color: "#362e2a",
                weight: calculateWeight(1.4),
            },
            tr: {
                fillColor: "#e59f48",
                opacity: 0,
                fillOpacity: 0.2,
                color: "#e59f48",
            },
        },
        carracci: {
            polygon: {
                color: "#107069",
                fillColor: "#00A19A",
                weight: 1,
                opacity: 0.5,
                fillOpacity: 0,
            },
            hover: {
                color: "#107069",
                fillColor: "#00A19A",
                weight: 1,
                opacity: 0.5,
                fillOpacity: 0.5,
            },
            selected: {
                color: "#107069",
                fillColor: "#00A19A",
                weight: 1,
                opacity: 0.5,
                fillOpacity: 0.5,
            },
        },
        depotEntity: {
            medagliere: {
                fillColor: DEPOT_STROKE_COLOR,
                color: "#72683D",
                weight: calculateWeight(3),
                opacity: 1,
                fillOpacity: 0.4,
            },
            museo: {
                fillColor: "#42A093",
                color: MUSEUM_STROKE_COLOR,
                weight: calculateWeight(3),
                opacity: 1,
                fillOpacity: 0.4,
            },
            spaziMuseali: {
                fillColor: "#38AA4A",
                color: "#339F44",
                weight: 1,
                opacity: 1,
                fillOpacity: 0.3,
            },
            heraDepot: {
                color: "#e76a00",
                fillColor: "#e76a00",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            heraShelf: {
                color: "#9A9F5B",
                fillColor: "#9A9F5B",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 1,
            },
            heraGround: {
                color: "#F38438",
                fillColor: "#F38438",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 1,
            },
            heraCart: {
                color: "#D9514F",
                fillColor: "#D9514F",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 1,
            },
            heraGrid: {
                color: "#5C8088",
                fillColor: "#5C8088",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 1,
                dashArray: "4 3",
            },
        },
        furniture: {
            medagliere: {
                fillColor: "#AAA163",
                color: "#72683D",
                weight: calculateWeight(3),
                opacity: 1,
                fillOpacity: 0.85,
            },
            museo: {
                fillColor: "#42A093",
                color: MUSEUM_STROKE_COLOR,
                weight: calculateWeight(3),
                opacity: 1,
                fillOpacity: 0.5,
            },
        },
        wall: {
            // temporary
            fillColor: DEPOT_STROKE_COLOR,
            color: "#5D752C",
            weight: calculateWeight(3),
            opacity: 1,
            fillOpacity: 0.7,
        },
        child: {
            fillColor: DEPOT_STROKE_COLOR,
            color: "#72683D",
            weight: calculateWeight(2),
            opacity: 0.6,
            fillOpacity: 0.2,
        },
        hoverChild: {
            medagliere: {
                fillColor: DEPOT_STROKE_COLOR,
                color: "#72683D",
                weight: calculateWeight(3),
                opacity: 1,
                fillOpacity: 0.5,
            },
            museo: {
                fillColor: "#42A093",
                color: MUSEUM_STROKE_COLOR,
                weight: calculateWeight(3),
                opacity: 1,
                fillOpacity: 0.8,
            },
            spaziMuseali: {
                fillColor: "#339F44",
                color: "#339F44",
                weight: 1,
                opacity: 1,
                fillOpacity: 0.5,
            },
            heraDepot: {
                color: "#e76a00",
                fillColor: "#e76a00",
                fillOpacity: 0.7,
                opacity: 1,
                weight: 4,
            },
            heraShelf: {
                color: "#9A9F5B",
                fillColor: "#9A9F5B",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 3,
            },
            heraGround: {
                color: "#F38438",
                fillColor: "#F38438",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 3,
            },
            heraCart: {
                color: "#D9514F",
                fillColor: "#D9514F",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 3,
            },
            heraGrid: {
                color: "#5C8088",
                fillColor: "#5C8088",
                fillOpacity: 0.6,
                opacity: 1,
                weight: 3,
                dashArray: "4 3",
            },
        },
        selectedNodeChild: {
            fillColor: DEPOT_STROKE_COLOR,
            color: "#72683D",
            weight: calculateWeight(4),
            opacity: 1,
            fillOpacity: 0.2,
        },
        environment: {
            fillColor: "#FFFF00",
            color: "#B2B200",
            weight: calculateWeight(1),
            opacity: 0.4,
            fillOpacity: 0.2,
        },
        topographicElement4: {
            fillColor: "#FF00FF",
            color: "#B200B2",
            weight: calculateWeight(1),
            opacity: 0.2,
            fillOpacity: 0.2,
        },
        monument: {
            color: "#D1A532",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.2,
            fillColor: "#FBD77B",
        },
        archaeologicalSite: {
            color: "#232323",
            weight: 1,
            opacity: 1,
            fillOpacity: 1,
            fillColor: "#E39441",
            radius: 6,
        },
        tomb: {
            color: "#232323",
            weight: 1,
            opacity: 1,
            fillOpacity: 1,
            fillColor: "#0e455d",
        },
        essay: {
            // saggio archeologico
            color: "#232323",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.6,
            fillColor: "#287393",
        },
        wes: {
            // cantiere di scavo
            color: "#232323",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.6,
            fillColor: "#9de2ed",
        },
        te1: {
            color: "#232323",
            weight: 1,
            opacity: 0,
            fillOpacity: 0.6,
            fillColor: "#efe8af",
        },
        te2: {
            color: "#232323",
            weight: 1,
            opacity: 0,
            fillOpacity: 0.6,
            fillColor: "#e2ae52",
        },
        us: {
            color: "#232323",
            weight: 1,
            opacity: 1,
            fillOpacity: 0.1,
            fillColor: "#ffffff",
        },
        hera: {
            monument: {
                color: "#308CAD",
                fillColor: "#308CAD",
                fillOpacity: 0.2,
                weight: 1,

            },
            investigation: {
                color: "#992E38",
                fillColor: "#992E38",
                fillOpacity: 0.2,
                weight: 1,
            }
        },
        pompei: {
            dsc: {
                color: "#B54A3A",
                fillColor: "#B54A3A",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_sas: {
                color: "#C97D5E",
                fillColor: "#C97D5E",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_us: {
                color: "#DFB852",
                fillColor: "#DFB852",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            tma_iccd: {
                color: "#2E79A4",
                fillColor: "#2E79A4",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            epigrafia: {
                color: "#D05042",
                fillColor: "#D05042",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_photo: {
                color: "#2F7FBC",
                fillColor: "#2F7FBC",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            unita_catastale: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            oggetto_iconografico: {
                color: "#8CA34D",
                fillColor: "#8CA34D",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            b: {
                color: "#2E74B7",
                fillColor: "#2E74B7",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_iconografia: {
                color: "#2C8D8F",
                fillColor: "#2C8D8F",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            referente_topografico: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_parete: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_ambiente: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_regio: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_insula: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_strada: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_struttura: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_pavimento: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_arredo: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_marciapiede: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            pompei_uc: {
                color: "#D88921",
                fillColor: "#D88921",
                fillOpacity: 0.2,
                opacity: 1,
                weight: 1,
            },
            multiLineString: {
                weight: calculateInverseWeight(3, 6),
            },
        },
    };
};
